import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, FileText, Send, X, CheckCircle, AlertCircle, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { useEmbeddedMode, postEmbeddedStepComplete } from '../hooks/useEmbeddedMode';

// Default passwords (used as fallback)
const DEFAULT_APPLICATION_PASSWORD = "ORBUplift1!";
const APPLICATION_URL = "https://www.collectiveorb.com/apply";

const SubmitApplicationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, api, refreshUser } = useAuth();
  const embedded = useEmbeddedMode();
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [progressPassword, setProgressPassword] = useState(DEFAULT_APPLICATION_PASSWORD);

  // Check if came from admin onboarding - check multiple sources
  const fromAdmin = location.state?.fromAdmin || 
    sessionStorage.getItem('admin_onboarding_return') === 'true' ||
    document.referrer.includes('/admin/onboarding');

  // Handle back navigation
  const handleBack = () => {
    // Clear the session flag when going back
    sessionStorage.removeItem('admin_onboarding_return');
    if (fromAdmin) {
      navigate('/admin/onboarding');
    } else {
      navigate('/');
    }
  };

  // Fetch current password from system settings
  useEffect(() => {
    const fetchPassword = async () => {
      try {
        const res = await api.get('/admin/onboarding/passwords');
        if (res.data?.step2a?.progress) {
          setProgressPassword(res.data.step2a.progress);
        }
      } catch (error) {
        // Use default password if fetch fails (non-admin users can't access this endpoint)
        setProgressPassword(DEFAULT_APPLICATION_PASSWORD);
      }
    };
    fetchPassword();
  }, [api]);

  // Check if already submitted
  const isApplicationSubmitted = user?.step2a_application_submitted;

  const handleSubmitApplication = () => {
    setShowApplicationForm(true);
  };

  const handleExitForm = () => {
    setShowApplicationForm(false);
    setShowPasswordDialog(true);
  };

  const handleVerifyPassword = async () => {
    if (!password) {
      toast.error('Please enter the password');
      return;
    }

    setVerifying(true);
    
    // Verify against the progress password
    try {
      const res = await api.post('/auth/verify-onboarding-password', {
        step_id: 'step2a',
        password: password
      });
      
      if (res.data.valid) {
        await api.put('/auth/me', { step2a_application_submitted: true });
        await refreshUser();
        toast.success('Application submitted successfully!');
        setShowPasswordDialog(false);
        if (embedded) postEmbeddedStepComplete('step2a_application_submitted');
        else navigate('/');
      } else {
        toast.error('Incorrect password. Please complete the application and use the confirmation password.');
      }
    } catch (error) {
      // Fallback to local check if API not available
      if (password === progressPassword || password === DEFAULT_APPLICATION_PASSWORD) {
        try {
          await api.put('/auth/me', { step2a_application_submitted: true });
          await refreshUser();
          toast.success('Application submitted successfully!');
          setShowPasswordDialog(false);
          if (embedded) postEmbeddedStepComplete('step2a_application_submitted');
          else navigate('/');
        } catch (e) {
          toast.error('Failed to update status. Please try again.');
        }
      } else {
        toast.error('Incorrect password. Please complete the application and use the confirmation password.');
      }
    }
    
    setVerifying(false);
  };

  const handleClosePasswordDialog = () => {
    setShowPasswordDialog(false);
    setPassword('');
  };

  // If already submitted, show success state
  if (isApplicationSubmitted) {
    return (
      <div className={embedded ? "p-2" : "min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4"}>
        <div className="max-w-md mx-auto pt-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleBack}
              className="rounded-full"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#1c1a1c] dark:text-white">Application Status</h1>
              <p className="text-sm text-[#676767] dark:text-gray-400">Submit Application</p>
            </div>
          </div>

          {/* Success Card */}
          <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-[#1c1a1c] dark:text-white">Application Submitted</h2>
              <p className="text-[#676767] dark:text-gray-400 mb-6">
                Your application has been successfully submitted. Please continue with the next onboarding steps.
              </p>
            </CardContent>
          </Card>

          {/* Return Button */}
          <Button 
            className="w-full mt-4 bg-[#2C3E50] hover:bg-[#34495E] text-white h-12"
            onClick={() => navigate('/')}
          >
            Return to Onboarding
          </Button>
        </div>
      </div>
    );
  }

  // Application Form Overlay (Full Screen Iframe) - Similar to Agreement Signing
  if (showApplicationForm) {
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #dfdfdf',
            backgroundColor: '#ffffff',
            flexShrink: 0
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <ClipboardList style={{ width: '20px', height: '20px', color: '#757150' }} />
            <div>
              <span style={{ fontWeight: 600, color: '#1c1a1c' }}>
                Submit Application
              </span>
              <p style={{ fontSize: '12px', color: '#676767', margin: 0 }}>
                Complete the application form
              </p>
            </div>
          </div>
          <Button 
            variant="outline"
            onClick={handleExitForm}
            className="gap-2 border-gray-300 text-gray-700 hover:bg-gray-100"
            data-testid="exit-application-form-btn"
          >
            <X className="w-4 h-4" />
            Exit & Enter Code
          </Button>
        </div>
        
        {/* Iframe Container - Zoomed to fill screen perfectly */}
        <div style={{ 
          flex: 1, 
          overflow: 'hidden',
          position: 'relative'
        }}>
          <iframe
            src={APPLICATION_URL}
            title="Application Form"
            allow="camera; microphone"
            scrolling="yes"
            style={{
              width: '73.5%',
              height: '73.5%',
              border: 'none',
              display: 'block',
              transform: 'scale(1.36)',
              transformOrigin: 'top left',
              position: 'absolute',
              top: 0,
              left: 0
            }}
          />
        </div>
      </div>
    );
  }

  // Main Application Page
  return (
    <div className={embedded ? "p-2" : "min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24"} data-testid="submit-application-page">
      <div className="max-w-md mx-auto pt-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleBack}
            className="rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1c1a1c] dark:text-white">Submit Application</h1>
            <p className="text-sm text-[#676767] dark:text-gray-400">Step 2A: Begin your journey</p>
          </div>
        </div>

        {/* Application Info Card */}
        <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg mb-4">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#e8ede6] dark:bg-[#2a2a2a] rounded-lg flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-[#757150]" />
              </div>
              <h2 className="text-xl font-bold text-[#1c1a1c] dark:text-white">Why Submit an Application?</h2>
            </div>

            <div className="space-y-4">
              <p className="text-[#676767] dark:text-gray-400">
                Welcome to The Uplift! To begin your onboarding journey, you need to <span className="font-semibold text-[#1c1a1c] dark:text-white">submit your application</span>. 
                This helps us learn more about you and ensure you're a great fit for our community.
              </p>

              <div className="bg-[#f5f5f5] dark:bg-[#1a1a1a] rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Send className="w-5 h-5 text-[#757150] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#1c1a1c] dark:text-white mb-1">What's Included</p>
                    <p className="text-sm text-[#676767] dark:text-gray-400">
                      You'll provide basic information about yourself, your background, and why you want to join ORB.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#f5f5f5] dark:bg-[#1a1a1a] rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-[#757150] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#1c1a1c] dark:text-white mb-1">After Submitting</p>
                    <p className="text-sm text-[#676767] dark:text-gray-400">
                      After you complete the application, you will receive a confirmation password. 
                      Return here and enter that password to complete this step.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Application Button */}
        <Button 
          className="w-full bg-[#2C3E50] hover:bg-[#34495E] text-white h-12 flex items-center justify-center gap-2"
          onClick={handleSubmitApplication}
          data-testid="submit-application-btn"
        >
          <ClipboardList className="w-4 h-4" />
          Submit Application
        </Button>

        {/* Already submitted link */}
        <button 
          onClick={() => setShowPasswordDialog(true)}
          className="w-full text-center mt-3 text-sm text-[#676767] hover:text-[#1c1a1c] dark:hover:text-white underline"
          data-testid="already-submitted-btn"
        >
          I've already submitted - Enter password
        </button>

        {/* Back to steps link */}
        <button 
          onClick={() => navigate('/')}
          className="w-full text-center mt-2 text-sm text-[#676767] hover:text-[#1c1a1c] dark:hover:text-white"
        >
          I'll do this later
        </button>
      </div>

      {/* Password Dialog - AI Personality Popup Style */}
      <Dialog open={showPasswordDialog} onOpenChange={handleClosePasswordDialog}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-lg font-bold">Enter Confirmation Password</DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Enter the password you received after submitting the application.
            </p>
          </DialogHeader>

          <div className="px-4 pb-4 space-y-3">
            <div className="bg-[#f5f5f5] dark:bg-[#2a2a2a] rounded-xl p-3">
              <div className="flex items-start gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-[#757150] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#676767] dark:text-gray-400">
                  If you haven't submitted the application yet, click "Submit Application" button first. 
                  The password will be provided after you complete the form.
                </p>
              </div>
              
              <Label className="text-xs text-[#676767] dark:text-gray-400">
                Confirmation Password
              </Label>
              <Input
                type="password"
                placeholder="Enter password from confirmation"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-9 rounded-xl"
                data-testid="application-password-input"
              />
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline"
                className="flex-1 h-10 rounded-xl"
                onClick={handleClosePasswordDialog}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 h-10 rounded-xl bg-[#2C3E50] hover:bg-[#34495E] text-white"
                onClick={handleVerifyPassword}
                disabled={verifying || !password}
                data-testid="verify-application-password-btn"
              >
                {verifying ? 'Verifying...' : 'Verify & Complete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubmitApplicationPage;
